import React from 'react';
import { createRoot } from 'react-dom/client';

import { UserAction } from '@cheerfy/lib/reactive/features/landing/models/customization';
import { ActionButtonsType } from '@cheerfy/lib/reactive/pages/landing/shared/actions.enum';
import { Navbar } from '@cheerfy/lib/reactive/features/landing/components/Navbar';
import { NavbarProps } from '@cheerfy/lib/reactive/features/landing/components/Navbar/hooks/useNavbar/context';
import { Environment, EnvironmentProvider } from '@cheerfy/lib/reactive/features/common/hooks/environment/context';

import { AuthButton, CommonAuthLanding } from '@cheerfy/lib/reactive/pages/landing/SharedLanding/AuthSharedLanding';
import { ScopeModel } from '@cheerfy/lib/reactive/features/api/models/scope';
import { AuthProvider } from '@cheerfy/lib/reactive/features/auth/components/AuthProvider';
import { LandingButtonAction } from '@cheerfy/lib/reactive/features/landing/components/LandingButton';
import { getAdapter } from '@cheerfy/lib/reactive/features/api/adapter';
import { CustomizationModel } from '@cheerfy/lib/reactive/features/landing/models/customization/customization.model';
import { CustomizationAdapter } from '@cheerfy/lib/reactive/features/landing/models/customization/customization.adapter';
import { mapSharedLandingDesignToNavbar } from '@cheerfy/lib/reactive/pages/landing/SharedLanding/utils';

import { getBtnActionText } from '@cheerfy/lib/reactive/pages/landing/SharedLanding/i18n-helpers';
import { I18N } from '@cheerfy/lib/js/i18n';

import { CustomerModel } from '@cheerfy/lib/reactive/features/api/models/customer/customer.model';
import { FormComponent } from '../components/cheerfypay/form/form.component';
import { PayConfiguration } from '../shared/types/backend/pay-configuration.type';

const i18nInstance = I18N.createI18nInstance([]);

export type AuthButtonContentOverride = {
    [K in ActionButtonsType]?: (context: UserAction) => LandingButtonAction
};

export function mapPayActionButtons(
    actions: UserAction[],
    lang: string,
    contentOverride?: AuthButtonContentOverride,
): AuthButton[] {
    const buttons: AuthButton[] = [];

    for (const action of actions) {
        buttons.push({
            type: action.type,
            text: action.texts?.[lang] || getBtnActionText(action.type, i18nInstance) || action.text,
            primary: action.highlight,
            content: contentOverride?.[action.type]?.(action) ?? action.content,
            icon: action.icon,
        });
    }

    return buttons;
}

export function getLang(supportedLanguages: string[]) {
    return I18N.getPriorityLanguage(
        navigator.languages?.map((lang) => lang.split('-')[0]) ?? [],
        supportedLanguages,
    );
}

export function getScopeFromLocation(location: number) {
    return new ScopeModel(
        undefined,
        [location],
    );
}

export function getEnvFromForm(): Environment {
    return {
        backendBaseUrl: FormComponent.getVariable('backendBaseUrl'),
        mapApiKey: '',
        servicesBaseUrl: FormComponent.getVariable('webhookBaseUrl'),
        servicesToken: FormComponent.getVariable('webhookToken'),
        staticsBaseUrl: '',
        portalBaseUrl: FormComponent.getVariable('portalBaseUrl'),
        rewardsBaseUrl: FormComponent.getVariable('rewardsBaseUrl'),
        ablyApiKey: FormComponent.getVariable('ablyApiKey'),
        paymentGatewayBaseUrl: '',
    };
}

export function paySettingsToCustomization(configuration: PayConfiguration, location: number): CustomizationModel {
    return getAdapter(CustomizationAdapter).adapt({
        ...configuration,
        scope: {
            locations: [location],
        },
    });
}

export function getNavbarProps(
    customizationModel: CustomizationModel,
    contentOverride?: AuthButtonContentOverride,
): Partial<NavbarProps> {
    const lang = getLang(customizationModel.settings.configuration.supportedLanguages);

    return {
        scope: customizationModel.scope,
        logo: {
            image: customizationModel.design.images.logo,
        },
        design: mapSharedLandingDesignToNavbar(customizationModel.design.colors as any),
        account: {
            actions: mapPayActionButtons(
                customizationModel?.settings?.user?.account.actions ?? [],
                lang,
                contentOverride,
            ),
        },
        profile: {
            texts: {
                editionHeader: customizationModel?.settings?.user?.account?.text?.[lang]?.title?.toString(),
                saveBtn: customizationModel?.settings?.user?.account?.text?.[lang]?.save?.toString(),
                cancelBtn: customizationModel?.settings?.user?.account?.text?.[lang]?.cancel?.toString(),
                emptyTagsNotice: customizationModel?.settings?.user?.account?.text?.[lang]?.add_tags?.toString(),
                labels: {
                    name: customizationModel?.settings?.user?.account?.text?.[lang]?.name?.toString(),
                    surname: customizationModel?.settings?.user?.account?.text?.[lang]?.surname?.toString(),
                    phone: customizationModel?.settings?.user?.account?.text?.[lang]?.phone?.toString(),
                    email: customizationModel?.settings?.user?.account?.text?.[lang]?.email?.toString(),
                    language: customizationModel?.settings?.user?.account?.text?.[lang]?.language?.toString(),
                    gender: customizationModel?.settings?.user?.account?.text?.[lang]?.gender?.toString(),
                    birthday: customizationModel?.settings?.user?.account?.text?.[lang]?.birthday?.toString(),
                    zipCode: customizationModel?.settings?.user?.account?.text?.[lang]?.post_code?.toString(),
                    tags: customizationModel?.settings?.user?.account?.text?.[lang]?.tags?.toString(),
                },
            },
        },
        ...(customizationModel.settings.menu.enabled ? {
            menu: {
                icon: customizationModel.design.images.menuIcon,
                props: {
                    showSeeProfileLink: customizationModel.settings.user.account.showLink,
                    sections: mapPayActionButtons(
                        customizationModel.settings.menu.sections as any ?? [],
                        lang,
                        contentOverride,
                    ),
                    texts: {
                        login: customizationModel.settings.menu.text?.[lang]?.login_link,
                        logout: customizationModel.settings.menu.text?.[lang]?.sign_out_link,
                        guestLabel: customizationModel.settings.menu.text?.[lang]?.text,
                    },
                    socials: customizationModel.settings.menu.socialNetworks.map((n) => ({
                        url: n.url,
                        rss: n.type,
                        img: n.icon,
                    })),
                },
            },
        } : {}),
    };
}

export function getCommonSharedLandingProps(
    data: PayConfiguration,
    location: number,
    contentOverride?: AuthButtonContentOverride,
): CommonAuthLanding {
    const adaptedSettings = paySettingsToCustomization(data, location);

    return ({
        buttons: [],
        rotative: {
            subtitles: [],
            titles: [],
        },
        backgroundImage: adaptedSettings.design.images.background,
        background640x1100: adaptedSettings.design.images.background640x1100,
        design: {
            font: adaptedSettings.design.fontFamily,
            general: adaptedSettings.design.colors.general,
            startPage: adaptedSettings.design.colors.startPage,
        },
        navbar: getNavbarProps(adaptedSettings, contentOverride),
    });
}

export function renderNavbar(
    data: PayConfiguration,
    location: number,
    onAuthChange?: (customer: CustomerModel | null, token: string | null) => void,
) {
    const adaptedSettings = paySettingsToCustomization(data, location);

    const sharedLandingProps = getCommonSharedLandingProps(data, location);

    const filteredLinks = sharedLandingProps.navbar.menu?.props
        .sections?.filter((l: any) => ![ActionButtonsType.PAY_LINK, ActionButtonsType.ORDER_LINK].includes(l.type))
        ?? [];

    const props = getNavbarProps(adaptedSettings);

    if (props.menu) {
        props.menu.props.sections = filteredLinks;
    }

    const navbarElement = React.createElement(Navbar, props);

    // eslint-disable-next-line react/no-children-prop
    const authProviderElement = React.createElement(AuthProvider, {
        scope: getScopeFromLocation(location),
        children: navbarElement,
        portalId: adaptedSettings.portalId,
        onAuthChange,
    });

    // eslint-disable-next-line react/no-children-prop
    const environmentElement = React.createElement(EnvironmentProvider, {
        env: getEnvFromForm(),
        children: authProviderElement,
    });

    const root = createRoot(<Element>document.getElementById('navbar-mnt'));

    root.render(environmentElement);
}
